import Icon, { IconName } from 'particles/Icon'
import styled, { useTheme } from 'particles/themes/styled'
import React from 'react'
import { ObjectiveType } from 'state/campaignTemplates'

interface ObjectiveIconProps {
    type: ObjectiveType
    isDisabled?: boolean
}

interface ObjectiveIconValue {
    color: string
    icon: IconName
}

const ObjectiveIcon: React.FC<ObjectiveIconProps> = ({ type, isDisabled = false }) => {
    const theme = useTheme()
    const iconMap: Record<ObjectiveType, ObjectiveIconValue> = {
        awareness: {
            color: theme.colors.indicator.ribbonRed,
            icon: 'eye',
        },
        traffic: {
            color: theme.colors.indicator.yolkYellow,
            icon: 'note',
        },
        credibility: {
            color: theme.colors.indicator.skyBlue,
            icon: 'handsClapping',
        },
        appPromotion: {
            color: theme.colors.indicator.darkGreen,
            icon: 'appStore',
        },
        content: {
            color: theme.colors.indicator.darkOrange,
            icon: 'videoCamera',
        },
    }
    const { color, icon } = iconMap[type]
    return (
        <IconContainer color={isDisabled ? theme.colors.surface.disable : color}>
            <Icon name={icon} color="actionInverse" size="24px" />
        </IconContainer>
    )
}

export default ObjectiveIcon

interface IconContainerProps {
    color: string
}

const IconContainer = styled.div<IconContainerProps>`
    background: ${props => props.color};
    border-radius: 8px;
    width: 40px;
    height: 40px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
`

export type IllustrativeIconType = 'template' | 'scratch'

export const IllustrativeIcon = ({ type, className }: { type: IllustrativeIconType; className?: string }) => {
    if (type === 'template') {
        return (
            <svg
                width="44"
                height="44"
                viewBox="0 0 44 44"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}
            >
                <g filter="url(#filter0_d_5293_46924)">
                    <path
                        d="M3 6C3 3.79086 4.79086 2 7 2L23.5882 2C25.7974 2 27.5882 3.79086 27.5882 6L27.5882 28C27.5882 30.2091 25.7974 32 23.5882 32L7 32C4.79086 32 3 30.2091 3 28L3 6Z"
                        fill="#FFD871"
                    />
                </g>
                <g filter="url(#filter1_d_5293_46924)">
                    <path
                        d="M16.4102 14C16.4102 11.7909 18.201 10 20.4102 10L36.9984 10C39.2075 10 40.9984 11.7909 40.9984 14L40.9984 36C40.9984 38.2091 39.2075 40 36.9984 40L20.4102 40C18.201 40 16.4102 38.2091 16.4102 36L16.4102 14Z"
                        fill="#F9A51A"
                    />
                </g>
                <path
                    opacity="0.4"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M27.5862 10L27.5862 28C27.5862 30.2091 25.7954 32 23.5862 32H16.4102L16.4102 14C16.4102 11.7909 18.201 10 20.4102 10H27.5862Z"
                    fill="#FFD871"
                />
                <defs>
                    <filter
                        id="filter0_d_5293_46924"
                        x="0"
                        y="0"
                        width="30.5879"
                        height="36"
                        filterUnits="userSpaceOnUse"
                        colorInterpolationFilters="sRGB"
                    >
                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                        <feColorMatrix
                            in="SourceAlpha"
                            type="matrix"
                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                            result="hardAlpha"
                        />
                        <feOffset dy="1" />
                        <feGaussianBlur stdDeviation="1.5" />
                        <feComposite in2="hardAlpha" operator="out" />
                        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0" />
                        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_5293_46924" />
                        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_5293_46924" result="shape" />
                    </filter>
                    <filter
                        id="filter1_d_5293_46924"
                        x="13.4102"
                        y="8"
                        width="30.5879"
                        height="36"
                        filterUnits="userSpaceOnUse"
                        colorInterpolationFilters="sRGB"
                    >
                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                        <feColorMatrix
                            in="SourceAlpha"
                            type="matrix"
                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                            result="hardAlpha"
                        />
                        <feOffset dy="1" />
                        <feGaussianBlur stdDeviation="1.5" />
                        <feComposite in2="hardAlpha" operator="out" />
                        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0" />
                        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_5293_46924" />
                        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_5293_46924" result="shape" />
                    </filter>
                </defs>
            </svg>
        )
    }

    return (
        <svg
            width="44"
            height="44"
            viewBox="0 0 44 44"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
        >
            <g filter="url(#filter0_d_5293_46931)">
                <path
                    d="M4 5.38462C4 4.06763 5.06763 3 6.38462 3L17.6499 3C18.9669 3 20.0345 4.06763 20.0345 5.38461L20.0345 16.6499C20.0345 17.9669 18.9669 19.0345 17.6499 19.0345L6.38462 19.0345C5.06763 19.0345 4 17.9669 4 16.6499L4 5.38462Z"
                    fill="#008BFF"
                />
            </g>
            <g filter="url(#filter1_d_5293_46931)">
                <path
                    d="M4 25.119C4 23.802 5.06763 22.7344 6.38462 22.7344L17.6499 22.7344C18.9669 22.7344 20.0345 23.802 20.0345 25.119L20.0345 36.3842C20.0345 37.7012 18.9669 38.7689 17.6499 38.7689L6.38462 38.7689C5.06763 38.7689 4 37.7012 4 36.3842L4 25.119Z"
                    fill="#253778"
                />
            </g>
            <g filter="url(#filter2_d_5293_46931)">
                <path
                    d="M23.7344 5.38461C23.7344 4.06763 24.802 3 26.119 3L37.3842 3C38.7012 3 39.7689 4.06763 39.7689 5.38461L39.7689 16.6499C39.7689 17.9669 38.7012 19.0345 37.3842 19.0345L26.119 19.0345C24.802 19.0345 23.7344 17.9669 23.7344 16.6499L23.7344 5.38461Z"
                    fill="#253778"
                />
            </g>
            <g filter="url(#filter3_d_5293_46931)">
                <path
                    d="M23.7344 25.119C23.7344 23.802 24.802 22.7344 26.119 22.7344L37.3842 22.7344C38.7012 22.7344 39.7689 23.802 39.7689 25.119L39.7689 36.3842C39.7689 37.7012 38.7012 38.7689 37.3842 38.7689L26.119 38.7689C24.802 38.7689 23.7344 37.7012 23.7344 36.3842L23.7344 25.119Z"
                    fill="#008BFF"
                />
            </g>
            <defs>
                <filter
                    id="filter0_d_5293_46931"
                    x="0.423077"
                    y="0.615385"
                    width="23.188"
                    height="23.188"
                    filterUnits="userSpaceOnUse"
                    colorInterpolationFilters="sRGB"
                >
                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                    <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                    />
                    <feOffset dy="1.19231" />
                    <feGaussianBlur stdDeviation="1.78846" />
                    <feComposite in2="hardAlpha" operator="out" />
                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0" />
                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_5293_46931" />
                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_5293_46931" result="shape" />
                </filter>
                <filter
                    id="filter1_d_5293_46931"
                    x="0.423077"
                    y="20.3498"
                    width="23.188"
                    height="23.188"
                    filterUnits="userSpaceOnUse"
                    colorInterpolationFilters="sRGB"
                >
                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                    <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                    />
                    <feOffset dy="1.19231" />
                    <feGaussianBlur stdDeviation="1.78846" />
                    <feComposite in2="hardAlpha" operator="out" />
                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0" />
                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_5293_46931" />
                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_5293_46931" result="shape" />
                </filter>
                <filter
                    id="filter2_d_5293_46931"
                    x="20.1575"
                    y="0.615385"
                    width="23.188"
                    height="23.188"
                    filterUnits="userSpaceOnUse"
                    colorInterpolationFilters="sRGB"
                >
                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                    <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                    />
                    <feOffset dy="1.19231" />
                    <feGaussianBlur stdDeviation="1.78846" />
                    <feComposite in2="hardAlpha" operator="out" />
                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0" />
                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_5293_46931" />
                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_5293_46931" result="shape" />
                </filter>
                <filter
                    id="filter3_d_5293_46931"
                    x="20.1575"
                    y="20.3498"
                    width="23.188"
                    height="23.188"
                    filterUnits="userSpaceOnUse"
                    colorInterpolationFilters="sRGB"
                >
                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                    <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                    />
                    <feOffset dy="1.19231" />
                    <feGaussianBlur stdDeviation="1.78846" />
                    <feComposite in2="hardAlpha" operator="out" />
                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0" />
                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_5293_46931" />
                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_5293_46931" result="shape" />
                </filter>
            </defs>
        </svg>
    )
}
